/**
 * The moderator ticket list as a query, shared by the list page and the detail page.
 *
 * The list keeps every filter in its URL (?tab=, ?validation=, ?claims=, ?escalation=, ?sort=,
 * ?page=). The detail page is handed that same query string, so "Back" lands on the exact list the
 * moderator came from and "Next ticket" walks the same filtered queue without re-deriving it.
 */

import type { Location } from 'react-router-dom'
import type { TicketQueryParams, TicketQuerySort } from '@api/services/tickets.api'
import { TicketEscalationLevel, TicketStatus, TicketType } from '@api/types/tickets'

export type ModeratorListTab = 'pool' | 'claimed' | 'assigned'

export const TICKET_SORTS: { value: TicketQuerySort; label: string }[] = [
  { value: 'priority', label: 'Priority' },
  { value: 'activity', label: 'Recent activity' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'score', label: 'Most votes' },
  { value: 'escalation', label: 'Most escalated' },
]

const SORT_VALUES = new Set<string>(TICKET_SORTS.map((s) => s.value))

/**
 * "Mine" lists keep a ticket until it is Closed (a Resolved one still needs its Close), the rule
 * the old claimed/assigned endpoints had. The pool is open tickets only.
 */
const NOT_CLOSED = Object.values(TicketStatus)
  .filter((v): v is TicketStatus => typeof v === 'number' && v !== TicketStatus.Closed)
  .join(',')

/** Escalation levels in the `?escalation=1,2` URL param. */
export function parseEscalation(raw: string | null): TicketEscalationLevel[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((x) => Number(x))
    .filter((x): x is TicketEscalationLevel => x === 0 || x === 1 || x === 2)
}

export function parseSort(raw: string | null): TicketQuerySort {
  return raw && SORT_VALUES.has(raw) ? (raw as TicketQuerySort) : 'priority'
}

export function parseListTab(raw: string | null): ModeratorListTab {
  return raw === 'claimed' || raw === 'assigned' ? raw : 'pool'
}

/** The server query for one list tab under the URL's filters; paging is added by the caller. */
export function ticketListQuery(params: URLSearchParams, tab: ModeratorListTab = parseListTab(params.get('tab'))): TicketQueryParams {
  const validationOnly = params.get('validation') === '1'
  const claimsOnly = params.get('claims') === '1'
  const escalation = parseEscalation(params.get('escalation'))
  const query: TicketQueryParams = {
    scope: 'queue',
    sort: parseSort(params.get('sort')),
    filter: validationOnly
      ? { validation: true }
      : claimsOnly
        ? { types: [TicketType.AdventureOwnershipClaim] }
        : undefined,
    escalation: escalation.length > 0 ? escalation : undefined,
  }
  if (tab === 'claimed') return { ...query, claimedBy: 'me', status: NOT_CLOSED }
  if (tab === 'assigned') return { ...query, assignedTo: 'me', status: NOT_CLOSED }
  return { ...query, status: 'open' }
}

/**
 * What the list hands the detail page in `location.state`: the list's query string (for Back and
 * for fetching further pages) and the ids it was showing, in order, with the offset of the first.
 * A snapshot rather than a live query on purpose: resolving a ticket drops it out of the pool, and
 * a live list would then shift "next" by one under the moderator's thumb.
 */
export interface TicketListContext {
  search: string
  ids: string[]
  skip: number
  total: number
}

export function readListContext(location: Location): TicketListContext | null {
  const state = location.state as { ticketList?: TicketListContext } | null
  const ctx = state?.ticketList
  return ctx && typeof ctx.search === 'string' && Array.isArray(ctx.ids) ? ctx : null
}

/** The list URL to go back to, with the filters the moderator left it with. */
export function ticketListHref(ctx: TicketListContext | null): string {
  return ctx?.search ? `/tickets?${ctx.search}` : '/tickets'
}
